import { el, clear } from "../components/dom.js";
import { getGameSeries } from "../api/igdb.js";
import { addGame, listGames, deleteGameByIgdbId, getGameByIgdbId } from "../db/games.js";
import { createManualGroup, listGroups, addItemToGroup, updateGroup } from "../db/groups.js";
import { navigate, navigateBack } from "../main.js";
import {
	MAG,
	magFeatureHero,
	magGlass,
	magButton,
	magEpisodeRow,
	magEmpty,
	magError,
	magLoading,
} from "../components/magazine/kit.js";

// Look up a game SERIES (IGDB collection) and present its games like episodes:
// each row is Add / Have, ordered by release. Adding a game also drops it into a
// Collection named after the series — mirrors the book-series feature.
export async function renderGameSeries(root, _match, collectionId) {
	const view = el("div.view", {});
	root.append(view);
	view.append(magLoading({ shape: "hero" }), magLoading({ shape: "list" }));

	let series;
	try {
		series = await getGameSeries(collectionId);
	} catch (err) {
		clear(view);
		view.append(magError({ title: "Couldn't load series", onRetry: () => renderGameSeries(root, _match, collectionId) }));
		return;
	}

	if (!series || (series.games ?? []).length === 0) {
		clear(view);
		view.append(
			el("div", { style: { paddingTop: "8px" } },
				magButton("‹ Back", { variant: "ghost", onClick: () => navigateBack("/games/add") })),
			magEmpty({
				title: "No games found",
				subtitle: "IGDB has no games listed for this series. Try the regular search instead.",
				actionLabel: "Back to search",
				onAction: () => navigateBack("/games/add"),
			}),
		);
		return;
	}

	await mount(view, series);
}

async function mount(view, series) {
	clear(view);

	const mine = await listGames();
	const ownedIds = new Set(mine.map((g) => g.igdb_id));
	const doneIds = new Set(mine.filter((g) => g.engagement_status === "done").map((g) => g.igdb_id));
	const isOwned = (g) => ownedIds.has(g.igdb_id);
	const isDone = (g) => doneIds.has(g.igdb_id);
	const ownedCount = series.games.filter(isOwned).length;
	const doneCount = series.games.filter(isDone).length;
	const total = series.games.length;
	const list_ = series.games;

	// Series completion (roadmap #39 / #40): finished so far, and what's next in order.
	const nextUp = list_.find((x) => !isDone(x));
	const completion = el("div", { style: { padding: "12px 16px 0" } },
		el("div", { style: { display: "flex", justifyContent: "space-between", fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11px", color: MAG.ink } },
			el("span", {}, `${doneCount} of ${total} played`),
			el("span", { style: { color: MAG.lime } }, total ? `${Math.round((doneCount / total) * 100)}%` : "")),
		el("div", { style: { height: "6px", borderRadius: "6px", background: "rgba(255,255,255,.1)", overflow: "hidden", marginTop: "6px" } },
			el("div", { style: { width: `${total ? (doneCount / total) * 100 : 0}%`, height: "100%", background: MAG.lime } })),
		nextUp && doneCount
			? el("div", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "11.5px", color: MAG.dim, marginTop: "6px" } }, `Next up: ${nextUp.name}`)
			: "");


	const coverItem = {
		media_type: "videogame",
		cover_url: series.games.find((g) => g.cover_url)?.cover_url ?? null,
	};

	const statusLine = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px",
			letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, padding: "10px 16px 0",
		},
	});

	async function addOne(game) {
		statusLine.textContent = `Adding ${game.name}…`;
		try {
			const record = await addSeriesGameToGroup(series, game);
			return record;
		} catch (err) {
			statusLine.textContent = `Could not add: ${err.message}`;
			return null;
		}
	}

	const addAllBtn = magButton(`+ Add all (${total - ownedCount})`, {
		variant: ownedCount >= total ? "owned" : "primary",
		onClick: async () => {
			addAllBtn.disabled = true;
			for (const game of series.games) {
				if (!isOwned(game)) await addOne(game);
			}
			await mount(view, series);
		},
	});

	const bar = magGlass(
		el("div", { style: { display: "flex", alignItems: "center", gap: "12px", width: "100%" } },
			el("div", { style: { flex: "1", minWidth: "0" } },
				el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", color: "#fff", lineHeight: ".9" } },
					`${total} game${total === 1 ? "" : "s"}`),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
						letterSpacing: ".1em", textTransform: "uppercase", color: MAG.lime, marginTop: "3px",
					},
				}, `${ownedCount} in your library`),
			),
			addAllBtn,
		),
		{ variant: "over-art", style: { padding: "12px 14px" } },
	);

	const hero = magFeatureHero(coverItem, {
		height: "240px",
		kicker: "Game series",
		title: series.title ?? "Series",
		onBack: () => navigateBack("/games/add"),
		bar,
	});

	const note = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "10px",
			letterSpacing: ".04em", color: MAG.faint, padding: "12px 16px 0",
		},
	}, `Adds land in a collection: ${series.title ?? "this series"}`);

	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "12px 16px 24px" } });
	series.games.forEach((game, i) => {
		const owned = isOwned(game);
		const yr = game.release_year ? ` · ${game.release_year}` : "";
		list.append(
			magEpisodeRow(
				{ title: game.name, cover_url: game.cover_url, media_type: "videogame" },
				{
					number: i + 1,
					kicker: `#${i + 1}${yr}`,
					owned,
					ownedLabel: "Have",
					addLabel: "+ Add",
					onAdd: async () => {
						if (owned) await deleteGameByIgdbId(game.igdb_id);
						else await addOne(game);
						await mount(view, series);
					},
				},
			),
		);
	});

	view.append(hero, completion, note, statusLine, list);
}

// Add a series game to the library AND into the series' collection. Reused by the
// collection view (group-detail) to add a "not yet added" suggestion. `series`
// needs { collection_id, title }.
export async function addSeriesGameToGroup(series, game) {
	let record = await getGameByIgdbId(game.igdb_id);
	if (!record) {
		record = await addGame({
			igdb_id: game.igdb_id,
			title: game.name,
			release_year: game.release_year ?? null,
			release_date: game.release_date ?? null,
			release_precision: game.release_precision ?? null,
			cover_url: game.cover_url ?? null,
			platforms: game.platforms ?? [],
			genres: game.genres ?? [],
			summary: game.summary ?? null,
			franchise: game.franchise ?? null,
			collection: game.collection ?? null,
		});
	}
	await ensureInCollection(series, record.id);
	return record;
}

// Reuse a same-named videogame collection if one exists, else create it; tag it
// with the IGDB collection id so the collection view can surface the rest of the
// series as dimmed suggestions.
async function ensureInCollection(series, itemId) {
	const name = series.title ?? "Series";
	const ref = series.collection_id != null ? `igdb:${series.collection_id}` : null;
	const groups = await listGroups();
	let group = groups.find((g) => g.media_type === "videogame" && (g.name ?? "").toLowerCase() === name.toLowerCase());
	if (!group) {
		await createManualGroup({ name, media_type: "videogame", member_ids: [itemId], series_ref: ref });
		return;
	}
	if (!group.series_ref && ref) {
		await updateGroup(group.id, { series_ref: ref });
	}
	await addItemToGroup(group.id, itemId);
}
